import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Tag,
  Modal,
  Input,
  Space,
  Alert,
  Table,
  Empty
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  TagOutlined,
  TeamOutlined,
  CalendarOutlined,
  GiftOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { internalCustomPlans } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import CopyableText from '../../components/internalAdmin/CopyableText';
import { paiseToINR } from '../../components/internalAdmin/AccountCells';

// Stage 5N-3: Custom Plan detail page.
//
// Shows the full picture of one Custom Plan + its redemption history.
// Available actions depend on (a) admin role permissions and (b) the
// plan's current state (active vs deactivated).

const { TextArea } = Input;

function StatusTag({ plan }) {
  if (!plan) return <Tag>—</Tag>;
  const expired = plan.validUntil && new Date(plan.validUntil).getTime() <= Date.now();
  if (expired) return <Tag color="default">Expired</Tag>;
  if (plan.isActive) return <Tag color="green">Active</Tag>;
  return <Tag color="red">Inactive</Tag>;
}

function RedemptionStatusTag({ status }) {
  const color = ({
    redeemed:  'green',
    reserved:  'blue',
    cancelled: 'default',
    expired:   'orange'
  })[status] || 'default';
  return <Tag color={color}>{status}</Tag>;
}

export default function CustomPlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [redemptions, setRedemptions] = useState({ items: [], total: 0 });
  const [redLoading, setRedLoading] = useState(true);

  const [activateOpen, setActivateOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [busy, setBusy] = useState(false);

  const canUpdate     = hasPermission('custom_plans.update');
  const canActivate   = hasPermission('custom_plans.activate');
  const canDeactivate = hasPermission('custom_plans.deactivate');
  const canViewRedemptions = hasPermission('custom_plans.redemptions.view');

  async function loadPlan() {
    setLoading(true);
    setError(null);
    try {
      const data = await internalCustomPlans.get(id);
      setPlan(data);
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load Custom Plan' });
    } finally {
      setLoading(false);
    }
  }

  async function loadRedemptions() {
    if (!canViewRedemptions) {
      setRedLoading(false);
      return;
    }
    setRedLoading(true);
    try {
      const data = await internalCustomPlans.listRedemptions(id, { limit: 100 });
      setRedemptions({
        items: Array.isArray(data.items) ? data.items : [],
        total: typeof data.total === 'number' ? data.total : 0
      });
    } catch (err) {
      // Don't show a popup — the redemptions card already shows an
      // empty state with explainer text in this case.
      setRedemptions({ items: [], total: 0 });
    } finally {
      setRedLoading(false);
    }
  }

  useEffect(() => {
    loadPlan();
    loadRedemptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function confirmActivate() {
    setBusy(true);
    try {
      await internalCustomPlans.activate(id);
      notifySuccess('Custom Plan activated', `${plan.customCode} is now active.`);
      setActivateOpen(false);
      loadPlan();
    } catch (err) {
      notifyError(err, { title: 'Could not activate Custom Plan' });
    } finally {
      setBusy(false);
    }
  }

  async function confirmDeactivate() {
    const reason = deactivateReason.trim();
    if (reason.length === 0) {
      notifyError(
        { code: 'VALIDATION_ERROR' },
        { title: 'Reason required', description: 'Enter a short reason.' }
      );
      return;
    }
    setBusy(true);
    try {
      await internalCustomPlans.deactivate(id, { reason });
      notifySuccess('Custom Plan deactivated', `${plan.customCode} is no longer redeemable.`);
      setDeactivateOpen(false);
      setDeactivateReason('');
      loadPlan();
    } catch (err) {
      notifyError(err, { title: 'Could not deactivate Custom Plan' });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingBlock tip="Loading Custom Plan…" />;

  if (error || !plan) {
    return (
      <div>
        <PageHeader title="Custom Plan" />
        <Alert
          type="error"
          showIcon
          message="Could not load Custom Plan"
          description="The Custom Plan may have been removed, or you may not have access."
          action={
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/internal/custom-plans')}
            >
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  const redemptionColumns = [
    {
      title: 'GSTIN',
      dataIndex: 'gstin',
      key: 'gstin',
      width: 160,
      render: (v) =>
        v ? <code className="text-[11px] text-ink-700">{v}</code> : <span className="text-ink-400">—</span>
    },
    {
      title: 'Tenant',
      key: 'tenant',
      width: 220,
      ellipsis: true,
      render: (_v, row) =>
        row.tenantId ? (
          <div className="leading-tight min-w-0">
            <button
              type="button"
              onClick={() => navigate(`/internal/accounts/${row.tenantId}`)}
              className="block max-w-full truncate text-left text-sm font-semibold text-brand-600 hover:text-brand-500"
              title={row.tenantLegalName || row.tenantDisplayName || ''}
            >
              {row.tenantLegalName || row.tenantDisplayName || '—'}
            </button>
            {row.tenantAccountStatus ? (
              <Tag bordered={false} style={{ fontSize: 10 }}>
                {row.tenantAccountStatus}
              </Tag>
            ) : null}
          </div>
        ) : (
          <span className="text-xs text-ink-500">No tenant yet</span>
        )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (v) =>
        v ? (
          <span className="text-xs text-ink-700" title={v}>
            {v}
          </span>
        ) : (
          <span className="text-ink-400">—</span>
        )
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (_v, row) =>
        row.paymentAmountPaise != null ? (
          <span className="text-xs font-medium text-ink-800">
            {paiseToINR(row.paymentAmountPaise)}
          </span>
        ) : (
          <span className="text-ink-400">—</span>
        )
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_v, row) => <RedemptionStatusTag status={row.status} />
    },
    {
      title: 'Razorpay',
      key: 'razorpay',
      width: 200,
      render: (_v, row) =>
        row.razorpayPaymentId ? (
          <CopyableText value={row.razorpayPaymentId} max={18} />
        ) : (
          <span className="text-ink-400">—</span>
        )
    },
    {
      title: 'Redeemed',
      key: 'redeemedAt',
      width: 170,
      render: (_v, row) => (
        <span className="text-xs text-ink-700">{formatDateTime(row.redeemedAt)}</span>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-2xl">{plan.customCode}</span>
            <StatusTag plan={plan} />
          </span>
        }
        subtitle={plan.planName}
        actions={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/internal/custom-plans')}
            >
              Back
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => { loadPlan(); loadRedemptions(); }}>
              Refresh
            </Button>
            <Button
              icon={<EditOutlined />}
              disabled={!canUpdate}
              onClick={() => navigate(`/internal/custom-plans/${id}/edit`)}
            >
              Edit
            </Button>
            {plan.publicSignupAllowed ? (
              <Button
                danger
                icon={<PauseCircleOutlined />}
                disabled={!canDeactivate}
                onClick={() => setDeactivateOpen(true)}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                disabled={!canActivate}
                onClick={() => setActivateOpen(true)}
              >
                Activate
              </Button>
            )}
          </Space>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          size="small"
          className="lg:col-span-2"
          title={
            <span className="flex items-center gap-2">
              <TagOutlined className="text-brand-500" />
              Plan details
            </span>
          }
        >
          <Descriptions column={2} size="small" colon>
            <Descriptions.Item label="Custom Code">
              <code className="text-sm font-bold">{plan.customCode}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Plan name">{plan.planName}</Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {plan.description || <span className="text-ink-400">—</span>}
            </Descriptions.Item>
            <Descriptions.Item label="Internal notes" span={2}>
              {plan.internalNotes ? (
                <span className="text-xs text-ink-700 whitespace-pre-wrap">
                  {plan.internalNotes}
                </span>
              ) : (
                <span className="text-ink-400">—</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Plan ID">
              <CopyableText value={plan.id} max={18} />
            </Descriptions.Item>
            <Descriptions.Item label="Internal plan code">
              <code className="text-[11px]">{plan.purchasePlanCode}</code>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={
            <span className="flex items-center gap-2">
              <TeamOutlined className="text-brand-500" />
              Pricing &amp; users
            </span>
          }
        >
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="Base amount">
              {paiseToINR(plan.basePricePaise)}
            </Descriptions.Item>
            <Descriptions.Item label="Discount">
              {plan.discountType === 'percent'
                ? <span className="text-rose-600">{Number(plan.discountPercent).toFixed(2)}% off ({paiseToINR(plan.discountPaise)})</span>
                : plan.discountType === 'fixed'
                  ? <span className="text-rose-600">{paiseToINR(plan.discountPaise)} off</span>
                  : <span className="text-ink-400">No discount</span>}
            </Descriptions.Item>
            <Descriptions.Item label="Taxable amount">
              {paiseToINR(plan.taxableAmountPaise)}
            </Descriptions.Item>
            <Descriptions.Item label="GST">
              {paiseToINR(plan.gstAmountPaise)} ({plan.gstRatePercent}%
              {plan.gstRateName ? ` · ${plan.gstRateName}` : ''})
            </Descriptions.Item>
            <Descriptions.Item label="Total payable">
              <span className="font-bold text-ink-900">
                {paiseToINR(plan.amountIncludingGstPaise)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Included / Max users">
              {plan.includedUsers} / {plan.maxUsers}
            </Descriptions.Item>
            <Descriptions.Item label="Paid duration">
              {plan.durationDays} days
            </Descriptions.Item>
            <Descriptions.Item label="Free months">
              {plan.freeMonths || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Total access">
              {plan.accessDurationDays} days
            </Descriptions.Item>
            <Descriptions.Item label="Currency">{plan.currency}</Descriptions.Item>
            <Descriptions.Item label="Payment required">
              <Tag color={plan.paymentRequired ? 'green' : 'default'}>
                {plan.paymentRequired ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={
            <span className="flex items-center gap-2">
              <CalendarOutlined className="text-brand-500" />
              Validity &amp; limits
            </span>
          }
        >
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="Valid from">
              {plan.validFrom ? formatDateTime(plan.validFrom) : 'Open-ended'}
            </Descriptions.Item>
            <Descriptions.Item label="Valid until">
              {plan.validUntil ? formatDateTime(plan.validUntil) : 'Open-ended'}
            </Descriptions.Item>
            <Descriptions.Item label="Max redemptions">
              {plan.maxRedemptions == null ? 'Unlimited' : plan.maxRedemptions}
            </Descriptions.Item>
            <Descriptions.Item label="Per GSTIN">
              {plan.maxRedemptionPerGstin}
            </Descriptions.Item>
            <Descriptions.Item label="Public signup">
              <Tag color={plan.publicSignupAllowed ? 'green' : 'red'}>
                {plan.publicSignupAllowed ? 'Allowed' : 'Blocked'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={
            <span className="flex items-center gap-2">
              <InfoCircleOutlined className="text-brand-500" />
              Customer restrictions
            </span>
          }
        >
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="GSTIN">
              {plan.restrictedGstin ? (
                <code className="text-[11px]">{plan.restrictedGstin}</code>
              ) : (
                <span className="text-ink-400">Open</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {plan.restrictedEmail || <span className="text-ink-400">Open</span>}
            </Descriptions.Item>
            <Descriptions.Item label="Mobile">
              {plan.restrictedMobile || <span className="text-ink-400">Open</span>}
            </Descriptions.Item>
            {plan.restrictedEmailDomain ? (
              <Descriptions.Item label="Email domain">
                {plan.restrictedEmailDomain}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
          <p className="text-[11px] text-ink-500 mt-3 mb-0 pt-3 border-t border-surface-200">
            Each restriction tightens who can redeem this Custom Code on
            public signup.
          </p>
        </Card>

        <Card
          size="small"
          title={
            <span className="flex items-center gap-2">
              <GiftOutlined className="text-brand-500" />
              Redemption activity
            </span>
          }
        >
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="Redemptions">
              <span className="font-bold">{plan.redemptionCount}</span>
              {plan.maxRedemptions != null ? (
                <span className="text-ink-500"> / {plan.maxRedemptions}</span>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label="Last redeemed">
              {plan.lastRedeemedAt ? formatDateTime(plan.lastRedeemedAt) : 'Never'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {formatDateTime(plan.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {formatDateTime(plan.updatedAt)}
            </Descriptions.Item>
            {plan.deactivatedAt ? (
              <Descriptions.Item label="Deactivated">
                <div className="leading-tight">
                  <p className="m-0 text-xs text-ink-700">
                    {formatDateTime(plan.deactivatedAt)}
                  </p>
                  {plan.deactivationReason ? (
                    <p className="m-0 text-[11px] text-ink-500 mt-1">
                      {plan.deactivationReason}
                    </p>
                  ) : null}
                </div>
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
      </div>

      <Card
        size="small"
        className="mt-4"
        title={
          <span className="flex items-center gap-2">
            <GiftOutlined className="text-brand-500" />
            Redemptions
            <Tag color="blue" bordered={false}>
              {redemptions.total}
            </Tag>
          </span>
        }
      >
        {!canViewRedemptions ? (
          <p className="text-sm text-ink-500 m-0">
            You do not have permission to view redemptions.
          </p>
        ) : redLoading ? (
          <LoadingBlock tip="Loading redemptions…" height={120} />
        ) : redemptions.items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No redemptions yet."
          />
        ) : (
          <Table
            rowKey="id"
            dataSource={redemptions.items}
            columns={redemptionColumns}
            size="small"
            pagination={false}
            scroll={{ x: 1100 }}
            tableLayout="fixed"
          />
        )}
      </Card>

      <Modal
        open={activateOpen}
        title="Activate Custom Plan?"
        okText="Activate"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmActivate}
        onCancel={() => (busy ? null : setActivateOpen(false))}
        maskClosable={!busy}
      >
        <p className="m-0">
          This makes <strong className="font-mono">{plan.customCode}</strong>{' '}
          redeemable again on public signup.
        </p>
      </Modal>

      <Modal
        open={deactivateOpen}
        title="Deactivate Custom Plan?"
        okText="Deactivate"
        okButtonProps={{
          danger: true,
          loading: busy,
          disabled: deactivateReason.trim().length === 0
        }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmDeactivate}
        onCancel={() => {
          if (busy) return;
          setDeactivateOpen(false);
          setDeactivateReason('');
        }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Deactivating <strong className="font-mono">{plan.customCode}</strong>{' '}
          blocks new public-signup redemptions. Existing tenants are unaffected.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (required)
        </label>
        <TextArea
          value={deactivateReason}
          onChange={(e) => setDeactivateReason(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="e.g. Promotion period ended"
          disabled={busy}
        />
      </Modal>
    </div>
  );
}
